<script lang="ts">
	import { fromJson, toJson } from "@bufbuild/protobuf";
	import { ValueSchema } from "@bufbuild/protobuf/wkt";
	import { Select, type SelectOptionType } from "flowbite-svelte-v1";
	import { getContext } from "svelte";
	import { DataType } from "#gen/common/v1/types_pb.js";
	import type { Component } from "#gen/pages/v2/page_pb.js";
	import Label from "#lib/components/Label.svelte";
	import { SettableAttributes, type BehaviorProps } from "#lib/components/flow/editor/builder/properties/page/constants.js";
	import DataTypeInput from "#lib/components/flow/editor/data-types/inputs/DataTypeInput.svelte";
	import { inputValueFromInputEvent } from "#lib/components/flow/editor/data-types/inputs/dataTypeInput.js";
	import type { Registry } from "#lib/components/page/registry.js";
	import { sentenceCase } from "#lib/string.js";

	const registry: Registry = getContext("registry");

	const { page, behavior, onChange }: BehaviorProps = $props();

	const components = $derived(flattenComponents(page?.components ?? []));
	const componentOptions: SelectOptionType<string>[] = $derived(
		components.map(component => ({
			name: registry.getDefinition(component.typeUri)?.displayName ?? sentenceCase(component.id),
			value: component.id,
		})),
	);
	const attributeOptions: SelectOptionType<string>[] = SettableAttributes.map(attribute => ({
		name: attribute.name,
		value: attribute.value,
	}));

	function flattenComponents(components: Component[]): Component[] {
		return components.reduce((acc: Component[], component: Component) => {
			const definition = registry.getDefinition(component.typeUri);
			if (!definition?.isContainer) {
				acc.push(component);
			}
			if (component.children) {
				acc.push(...flattenComponents(component.children));
			}
			return acc;
		}, []);
	}

	function handleComponentChange(event: Event) {
		const componentId = (event.target as HTMLSelectElement).value;
		if (behavior.action?.value?.value) {
			behavior.action.value.value.component = componentId;
			onChange?.(behavior);
		}
	}

	function handleAttributeChange(event: Event) {
		const attribute = (event.target as HTMLSelectElement).value;
		if (behavior.action?.value?.value) {
			behavior.action.value.value.attribute = attribute;
			onChange?.(behavior);
		}
	}

	function handleValueChange(event: Event) {
		let value = inputValueFromInputEvent(event);
		if (behavior.action?.value?.value?.attribute && getDataTypeForAttribute(behavior.action?.value?.value?.attribute) === DataType.BOOLEAN) {
			value = value === "true";
		}
		if (behavior.action?.value?.value) {
			behavior.action.value.value.value = fromJson(ValueSchema, value ?? null);
			onChange?.(behavior);
		}
	}

	function getDataTypeForAttribute(attribute: string): DataType {
		return SettableAttributes.find(option => option.value === attribute)?.dataType ?? DataType.STRING;
	}
</script>

<form class="grid grid-cols-2 gap-2">
	<div class="flex min-w-0 flex-1 flex-col gap-1">
		<Label for="component" class="text-sm font-medium text-gray-700">Component</Label>
		<Select id="component" value={behavior.action?.value?.value?.component ?? ""} items={componentOptions} onchange={e => handleComponentChange(e)} />
	</div>
	<div class="flex min-w-0 flex-1 flex-col gap-1">
		<Label for="attribute" class="text-sm font-medium text-gray-700">Attribute</Label>
		<Select id="attribute" value={behavior.action?.value?.value?.attribute ?? ""} items={attributeOptions} onchange={e => handleAttributeChange(e)} />
	</div>
	{#if behavior.action?.value?.value?.attribute}
		<div class="flex min-w-0 flex-1 flex-col gap-1">
			<Label for="attribute" class="text-sm font-medium text-gray-700">Value</Label>
			<DataTypeInput dataType={getDataTypeForAttribute(behavior.action?.value?.value?.attribute)} value={behavior.action?.value?.value?.value ? toJson(ValueSchema, behavior.action?.value?.value?.value) : undefined} onchange={e => handleValueChange(e)} />
		</div>
	{/if}
</form>
